import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Badge,
  Button,
  Checkbox,
  Tooltip,
  Text,
  Spinner,
} from '@fluentui/react-components';
import { Star16Filled, StarAdd20Regular, Dismiss20Regular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import CompanyPicker from '../CompanyPicker';
import { queryKeys } from '../../lib/queryClient';
import {
  addCustomerCompany,
  removeCustomerCompany,
  setPrimaryCompany,
  setMembershipFlags,
  type LinkedCompany,
} from '../../services/customerService';

interface LinkedCompaniesEditorProps {
  /** The contact being edited (id-of-record — TWM or Booqable id both resolve). */
  contactId: string;
  /** Current memberships (primary + secondary). */
  linkedCompanies: LinkedCompany[];
  /** Report the full set back to the form so the primary stays in sync. */
  onChange: (companies: LinkedCompany[]) => void;
  /** Red border on the add-picker when the contact has no company (validation). */
  error?: boolean;
  disabled?: boolean;
}

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
    ...shorthands.padding('6px', '8px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  rowTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('8px'),
  },
  flagsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: '16px',
    rowGap: '2px',
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    minWidth: 0,
  },
  link: {
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    fontWeight: 600,
    cursor: 'pointer',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ':hover': { textDecoration: 'underline' },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
    flexShrink: 0,
  },
  addLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
});

/**
 * Edit-mode control for a contact's company memberships. A contact may belong
 * to more than one company; exactly one is the primary (starred, and what
 * orders / kind-detection roll up to). Mutations hit the /companies collection
 * endpoints live and keep a local copy in sync so the list reflects changes
 * without a full drawer refetch. Reports the set back via onChange so the
 * form's primary linkedCompanyId stays correct.
 */
const LinkedCompaniesEditor: React.FC<LinkedCompaniesEditorProps> = ({
  contactId,
  linkedCompanies,
  onChange,
  error,
  disabled,
}) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [companies, setCompanies] = useState<LinkedCompany[]>(linkedCompanies);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Re-seed when the drawer loads a different contact / fresh data arrives.
  useEffect(() => { setCompanies(linkedCompanies); }, [linkedCompanies]);

  // Sort primary-first, then name — stable display.
  const ordered = [...companies].sort(
    (a, b) => (Number(b.isPrimary) - Number(a.isPrimary)) || a.name.localeCompare(b.name)
  );

  const commit = (next: LinkedCompany[]) => {
    setCompanies(next);
    onChange(next);
    // Kind/avatars + company option lists elsewhere depend on the link set.
    queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    queryClient.invalidateQueries({ queryKey: ['linked-company-options'] });
  };

  const handleAdd = async (companyId: string | null, companyName: string) => {
    if (!companyId || busy) return;
    if (companies.some((c) => c.id === companyId)) return; // already linked
    setBusy(true);
    setErr(null);
    try {
      await addCustomerCompany(contactId, companyId);
      const firstLink = companies.length === 0;
      commit([...companies, {
        id: companyId,
        name: companyName,
        isPrimary: firstLink,
        isBillingContact: false,
        isMainContact: false,
        emailOptIn: false,
      }]);
    } catch (e: any) {
      setErr(e?.message || 'Failed to add company.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (companyId: string) => {
    setBusy(true);
    setErr(null);
    try {
      await removeCustomerCompany(contactId, companyId);
      const removed = companies.find((c) => c.id === companyId);
      let next = companies.filter((c) => c.id !== companyId);
      // Mirror the backend's promote-oldest-survivor when the primary leaves.
      if (removed?.isPrimary && next.length > 0 && !next.some((c) => c.isPrimary)) {
        next = next.map((c, i) => (i === 0 ? { ...c, isPrimary: true } : c));
      }
      commit(next);
    } catch (e: any) {
      setErr(e?.message || 'Failed to remove company.');
    } finally {
      setBusy(false);
    }
  };

  const handleMakePrimary = async (companyId: string) => {
    setBusy(true);
    setErr(null);
    try {
      await setPrimaryCompany(contactId, companyId);
      commit(companies.map((c) => ({ ...c, isPrimary: c.id === companyId })));
    } catch (e: any) {
      setErr(e?.message || 'Failed to set primary company.');
    } finally {
      setBusy(false);
    }
  };

  // Per-membership: a contact can be the billing contact for MANY companies, so
  // toggling one company never touches this contact's other company rows. The
  // "one billing contact per company" rule is about OTHER contacts and is
  // enforced server-side (it demotes the prior billing contact on that company).
  // Setting billing true also opts this membership into email (mirrors backend).
  // Optimistic: flip the checkbox immediately and save in the background — these
  // are cheap per-membership flags, so don't gate the whole editor on `busy`
  // (which greys every control for the request's duration). Revert only on error.
  const handleToggleBilling = (companyId: string, checked: boolean) => {
    setErr(null);
    const prev = companies;
    commit(
      companies.map((c) =>
        c.id === companyId
          ? (checked
              ? { ...c, isBillingContact: true, emailOptIn: true }
              : { ...c, isBillingContact: false })
          : c
      )
    );
    setMembershipFlags(contactId, companyId, { is_billing_contact: checked }).catch((e: any) => {
      setErr(e?.message || 'Failed to update billing contact.');
      commit(prev);
    });
  };

  // Main contact is one-per-company (like billing): the server demotes any
  // other contact holding it on this company. Toggling here only affects this
  // contact's row for this company. Optimistic, same as billing.
  const handleToggleMainContact = (companyId: string, checked: boolean) => {
    setErr(null);
    const prev = companies;
    commit(companies.map((c) => (c.id === companyId ? { ...c, isMainContact: checked } : c)));
    setMembershipFlags(contactId, companyId, { main_contact: checked }).catch((e: any) => {
      setErr(e?.message || 'Failed to update main contact.');
      commit(prev);
    });
  };

  const handleToggleEmail = (companyId: string, checked: boolean) => {
    setErr(null);
    const prev = companies;
    commit(companies.map((c) => (c.id === companyId ? { ...c, emailOptIn: checked } : c)));
    setMembershipFlags(contactId, companyId, { email_opt_in: checked }).catch((e: any) => {
      setErr(e?.message || 'Failed to update email opt-in.');
      commit(prev);
    });
  };

  return (
    <div className={styles.wrapper}>
      {ordered.map((c) => (
        <div key={c.id} className={styles.row}>
          <div className={styles.rowTop}>
            <div className={styles.nameCell}>
              <a
                href={`/customers/${c.id}`}
                className={styles.link}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                  e.preventDefault();
                  navigate(`/customers/${c.id}`);
                }}
              >
                {c.name}
              </a>
              {c.isPrimary && (
                <Badge appearance="tint" color="brand" icon={<Star16Filled />} size="small">
                  Primary
                </Badge>
              )}
            </div>
            <div className={styles.actions}>
              {!c.isPrimary && (
                <Tooltip content="Make primary company" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<StarAdd20Regular />}
                    aria-label={`Make ${c.name} the primary company`}
                    disabled={busy || disabled}
                    onClick={() => handleMakePrimary(c.id)}
                  />
                </Tooltip>
              )}
              <Tooltip content="Unlink company" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Dismiss20Regular />}
                  aria-label={`Unlink ${c.name}`}
                  disabled={busy || disabled}
                  onClick={() => handleRemove(c.id)}
                />
              </Tooltip>
            </div>
          </div>
          {/* Per-membership designations FOR THIS company. Billing contact and
              main contact are each one-per-company; billing also forces email
              on. */}
          <div className={styles.flagsRow}>
            <Checkbox
              label="Main contact"
              checked={c.isMainContact}
              disabled={busy || disabled}
              onChange={(_e, data) => handleToggleMainContact(c.id, !!data.checked)}
            />
            <Checkbox
              label="Billing contact"
              checked={c.isBillingContact}
              disabled={busy || disabled}
              onChange={(_e, data) => handleToggleBilling(c.id, !!data.checked)}
            />
            <Checkbox
              label="Receives email"
              checked={c.emailOptIn || c.isBillingContact}
              disabled={busy || disabled || c.isBillingContact}
              onChange={(_e, data) => handleToggleEmail(c.id, !!data.checked)}
            />
          </div>
        </div>
      ))}

      {busy && <Spinner size="tiny" label="Saving…" />}

      <Text className={styles.addLabel}>Add a company</Text>
      <CompanyPicker
        selectedId={null}
        onChange={handleAdd}
        disabled={busy || disabled}
        error={error && companies.length === 0}
      />

      {err && (
        <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
          {err}
        </Text>
      )}
    </div>
  );
};

export default LinkedCompaniesEditor;
